import {useCallback, useState} from "react";
import {observer} from "mobx-react";
import type {Color} from "../../../../../generated/api/base";
import type {XyiconManager} from "../../../../modules/space/spaceeditor/logic3d/managers/spaceitems/XyiconManager";
import type {BoundaryManager} from "../../../../modules/space/spaceeditor/logic3d/managers/spaceitems/BoundaryManager";
import type {IFieldAdapter} from "../../../../../data/models/field/Field";
import {featureTitles} from "../../../../../data/state/AppStateConstants";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {XyiconFeature} from "../../../../../generated/api/base";
import {CheckboxInput} from "../../../../widgets/input/checkbox/CheckboxInput";
import {ButtonV5} from "../../../button/ButtonV5";
import {SearchFieldV5} from "../../../input/search/SearchFieldV5";
import {ToggleContainerV5} from "../../../widgets/ToggleContainerV5/ToggleContainerV5";
import type {SupportedFontName} from "../../../../../data/state/AppStateTypes";
import {HeaderButtonsStyled} from "../../../filter/FilterEditorV5";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import CloseIcon from "../../../icons/xmark.svg?react";
import LabelIcon from "../../../icons/label-icon.svg?react";
import type {ICaptionSettings} from "../../../../../data/models/ViewUtils";
import {isCaptionSupportedForField} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {IndividualCaptionStyleV5} from "./IndividualCaptionStyleV5";
import {CaptionSettingsV5} from "./CaptionSettingsV5";
import {CaptionPanel} from "./CaptionPanel.styled";
import type {IOverlayPanelProps} from "./LayersPanelV5";
import {PanelHeaderStyled} from "./LayerPanel.styled";

interface IExtendedOverlayPanelProps extends IOverlayPanelProps {
	readonly onConfiguratorOpenChange?: (isOpen: boolean) => void;
}

export const CaptionPanelV5 = observer((props: IExtendedOverlayPanelProps) => {
	const {spaceViewRenderer, onConfiguratorOpenChange, view, onClose} = props;

	const [searchString, setSearchString] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const selectedSpaceEditorView = spaceViewRenderer.actions.getSelectedView(XyiconFeature.SpaceEditor);
	const feature = XyiconFeature.SpaceEditor;
	const viewKey = `${feature}-${selectedSpaceEditorView?.id ?? "unknown"}`;

	const captionConfig = view.spaceEditorViewSettings.captions;
	const getCaptionSettings = (itemType: "boundary" | "xyicon") => captionConfig[itemType];

	const savedCaptionConfig = view.getSavedSpaceEditorViewSettings()?.captions || view.getDefaultCaptionConfig();

	const updateCaptionSetting = <K extends keyof ICaptionSettings>(key: K, value: ICaptionSettings[K], shouldUpdateCaptions = false) => {
		getCaptionSettings("boundary")[key] = value;
		getCaptionSettings("xyicon")[key] = value;

		if (selectedSpaceEditorView === view) {
			const {boundaryManager, xyiconManager} = spaceViewRenderer;

			if (shouldUpdateCaptions) {
				updateCaptionsIfNecessary();
			} else {
				boundaryManager.captionManager.updateTextTransformations();
				xyiconManager.captionManager.updateTextTransformations();
			}
		}
	};

	const updateCaptionsIfNecessary = () => {
		if (selectedSpaceEditorView === view) {
			const {boundaryManager, xyiconManager} = spaceViewRenderer;

			updateCaptions([boundaryManager, xyiconManager]);
		}
	};

	const onCaptionFontColorChange = (newColor: Color) => updateCaptionSetting("fontColor", newColor);
	const onCaptionBackgroundColorChange = (newColor: Color) => updateCaptionSetting("backgroundColor", newColor);
	const onCaptionFontSizeChange = (newSize: number) => updateCaptionSetting("fontSize", newSize, true);
	const onCaptionFontFamilyChange = (newFont: SupportedFontName) => updateCaptionSetting("fontFamily", newFont, true);
	const onCaptionIsBoldChange = (isBold: boolean) => updateCaptionSetting("isBold", isBold, true);
	const onCaptionIsItalicChange = (isItalic: boolean) => updateCaptionSetting("isItalic", isItalic, true);
	const onCaptionIsUnderlinedChange = (isUnderlined: boolean) => updateCaptionSetting("isUnderlined", isUnderlined, true);

	const activeItemManager = (feature: XyiconFeature) =>
		feature === XyiconFeature.Xyicon ? props.spaceViewRenderer.xyiconManager : props.spaceViewRenderer.boundaryManager;

	const updateCaptions = (itemManagers: (XyiconManager | BoundaryManager)[]) => {
		setIsLoading(true);
		// Make sure we show the change in the UI before doing any heavy process that can freeze up the app for a long time
		requestAnimationFrame(() => {
			requestAnimationFrame(async () => {
				for (const itemManager of itemManagers) {
					await itemManager.captionManager.updateCaptions();
				}

				requestAnimationFrame(() => setIsLoading(false));
			});
		});
	};

	const getLayoutFieldIds = (feature: XyiconFeature) => {
		const fieldIds: string[] = [];
		const layout = spaceViewRenderer.actions.getLayout(feature);

		if (layout?.sections) {
			for (const section of layout.sections) {
				for (const field of section.fields) {
					if (!fieldIds.includes(field.id)) {
						fieldIds.push(field.id);
					}
				}
			}
		}

		return fieldIds;
	};

	const getFieldsWithCheckboxes = (feature: XyiconFeature) => {
		const fieldIdsInLayout = getLayoutFieldIds(feature);
		const fields = spaceViewRenderer.actions
			.getFieldsByFeature(feature, true)
			.filter((field: IFieldAdapter) => fieldIdsInLayout.includes(field.refId));

		// some fields are not present in the layout section, like id, type, etc.
		// Add own (non inherited) fields if they're not added yet
		const ownFields = spaceViewRenderer.actions.getFieldsByFeature(feature, false);

		for (const field of ownFields) {
			if (!fields.includes(field)) {
				fields.push(field);
			}
		}

		const itemManager = activeItemManager(feature);
		const itemType = feature === XyiconFeature.Xyicon ? "xyicon" : "boundary";
		const captionSettingsMaybe = getCaptionSettings(itemType);
		const captionFields = captionSettingsMaybe?.checkList || [];
		const sortedElements = fields.toSorted((a: IFieldAdapter, b: IFieldAdapter) => StringUtils.sortIgnoreCase(a.name, b.name));
		const fieldNames = fields.map((field) => field.name);

		return sortedElements
			.filter((field: IFieldAdapter) => isCaptionSupportedForField(field))
			.filter((field: IFieldAdapter) => StringUtils.containsIgnoreCase(field.name, searchString))
			.map((field: IFieldAdapter, index: number) => {
				const needModuleName = fieldNames.filter((name) => name === field.name).length > 1;
				const captionLabel = `${field.name}${needModuleName ? ` (${featureTitles[field.feature]})` : ""}`;

				return (
					<div
						className="hbox checkBoxContainer alignCenter"
						key={field.refId}
					>
						<CheckboxInput
							label={field.name}
							disabled={isLoading}
							value={captionFields.includes(field.refId)}
							onChange={(value: boolean) => {
								if (value) {
									captionFields.push(field.refId);
								} else {
									const index = captionFields.indexOf(field.refId);

									if (index > -1) {
										captionFields.splice(index, 1);
									}
								}

								if (selectedSpaceEditorView === view) {
									updateCaptions([itemManager]);
								}
							}}
						/>
						<div className="checkBoxLabel">{captionLabel}</div>
						<IndividualCaptionStyleV5
							appState={spaceViewRenderer.transport.appState}
							captionLabel={captionLabel}
							fieldRefId={field.refId}
							captionSettings={captionSettingsMaybe}
							onConfiguratorOpenChange={onConfiguratorOpenChange}
						/>
					</div>
				);
			});
	};

	const isResetButtonEnabled = useCallback(() => {
		return JSON.stringify(captionConfig) !== JSON.stringify(savedCaptionConfig);
	}, [captionConfig, savedCaptionConfig]);

	const onResetClick = useCallback(async () => {
		view.spaceEditorViewSettings.captions = savedCaptionConfig;
		await spaceViewRenderer.boundaryManager.captionManager.updateCaptions();
		await spaceViewRenderer.xyiconManager.captionManager.updateCaptions();
		sessionStorage.setItem(viewKey, "0");
	}, [savedCaptionConfig, spaceViewRenderer, view, viewKey]);

	const onSearchInput = (searchString: string) => {
		setSearchString(searchString);
	};

	const hasChanges = selectedSpaceEditorView.hasUnsavedChanges("captions", false);

	return (
		<CaptionPanel>
			<div className="container">
				<PanelHeaderStyled>
					<div className="title">
						<LabelIcon />
						<h4>LABELS</h4>
					</div>
					<HeaderButtonsStyled>
						{hasChanges && (
							<ButtonV5
								type="secondary"
								label="Reset"
								disabled={!isResetButtonEnabled()}
								onClick={onResetClick}
							/>
						)}
						<IconButtonV5
							IconComponent={CloseIcon}
							onClick={onClose}
						/>
					</HeaderButtonsStyled>
				</PanelHeaderStyled>
				<div className="head hbox">
					<SearchFieldV5
						className="SearchField"
						value={searchString}
						onInput={onSearchInput}
						fullWidth
					/>
				</div>
				<div className="body">
					<div className="checkBoxes">
						<ToggleContainerV5
							title="Boundaries"
							open={true}
						>
							{getFieldsWithCheckboxes(XyiconFeature.Boundary)}
						</ToggleContainerV5>
						<ToggleContainerV5
							title="Xyicons"
							open={true}
						>
							{getFieldsWithCheckboxes(XyiconFeature.Xyicon)}
						</ToggleContainerV5>
					</div>
					<ToggleContainerV5
						title="Label Settings"
						open={true}
						className="CaptionSettingsContainer"
					>
						<CaptionSettingsV5
							captionSettings={getCaptionSettings("xyicon")}
							isLoading={isLoading}
							onCaptionBackgroundColorChange={onCaptionBackgroundColorChange}
							onCaptionFontColorChange={onCaptionFontColorChange}
							onCaptionFontSizeChange={onCaptionFontSizeChange}
							onCaptionFontFamilyChange={onCaptionFontFamilyChange}
							onCaptionIsBoldChange={onCaptionIsBoldChange}
							onCaptionIsItalicChange={onCaptionIsItalicChange}
							onCaptionIsUnderlinedChange={onCaptionIsUnderlinedChange}
							spaceViewRenderer={spaceViewRenderer}
						/>
					</ToggleContainerV5>
				</div>
			</div>
		</CaptionPanel>
	);
});
